import { useTranslation } from 'react-i18next';

import { formatDateTime, formatNumber } from '../i18n.js';

export interface UptimePoint {
  t: string;
  checks: number;
  failed: number;
  avgResponseMs: number | null;
}

const WIDTH = 720;
const HEIGHT = 160;

/**
 * Response time bars; buckets with failed checks are red (and taller than the scale, with a
 * pattern-free distinct colour plus text summary, so colour is not the only signal).
 */
export function UptimeChart({
  points,
  uptimePct,
  timeZone,
}: {
  points: UptimePoint[];
  uptimePct: number | null;
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  if (points.length === 0)
    return <p className="py-8 text-center text-slate-700">{t('site.chart.empty')}</p>;

  const values = points.map((p) => p.avgResponseMs ?? 0);
  const max = Math.max(500, ...values);
  const barWidth = WIDTH / points.length;
  const failedBuckets = points.filter((p) => p.failed > 0).length;
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
  const summary = [
    uptimePct === null
      ? null
      : t('site.chart.uptime', { value: formatNumber(uptimePct, i18n.language) }),
    `${t('site.responseTime')} (Median): ${median} ms`,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <figure>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-40 w-full"
        role="img"
        aria-label={`${summary}. ${t('site.chart.description')}`}
        preserveAspectRatio="none"
      >
        <line x1="0" y1={HEIGHT - 0.5} x2={WIDTH} y2={HEIGHT - 0.5} stroke="#94a3b8" />
        {points.map((p, i) => {
          const failed = p.failed > 0;
          const h = failed ? HEIGHT : Math.max(2, ((p.avgResponseMs ?? 0) / max) * (HEIGHT - 8));
          return (
            <rect
              key={p.t}
              x={i * barWidth + barWidth * 0.1}
              y={HEIGHT - h}
              width={Math.max(1, barWidth * 0.8)}
              height={h}
              fill={failed ? '#b91c1c' : '#1d4ed8'}
              opacity={failed ? 0.85 : 0.75}
            >
              <title>
                {t('site.chart.point', {
                  time: formatDateTime(p.t, i18n.language, timeZone),
                  ms: p.avgResponseMs ?? '–',
                  failed: p.failed,
                  checks: p.checks,
                })}
              </title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="mt-2 flex flex-wrap gap-x-4 text-sm text-slate-800">
        <span>{summary}</span>
        {failedBuckets > 0 ? (
          <span className="inline-flex items-center gap-1">
            <span aria-hidden="true" className="inline-block h-3 w-3 rounded-sm bg-red-700" />
            {t('site.chart.description')}
          </span>
        ) : null}
      </figcaption>
    </figure>
  );
}
